'use client';

import React, { ReactNode } from 'react';
import styles from './sidebar.module.sass';

export interface SidebarItem {
  label: string;
  icon?: ReactNode;
  href: string;
  active?: boolean;
  badge?: string | number;
  chevron?: boolean;
}

export interface SidebarProps {
  items: SidebarItem[];
  header?: ReactNode;
  collapsed?: boolean;
  onItemClick?: (item: SidebarItem) => void;
  ctaTitle?: string;
  ctaDescription?: string;
  ctaButtonLabel?: string;
  onCtaClick?: () => void;
  footerItems?: SidebarItem[];
}

const Sidebar = React.forwardRef<HTMLDivElement, SidebarProps>(
  (
    {
      items,
      header,
      collapsed = false,
      onItemClick,
      ctaTitle,
      ctaDescription,
      ctaButtonLabel,
      onCtaClick,
      footerItems = [],
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={`${styles.sidebar} ${collapsed ? styles.collapsed : ''}`}
      >
        {/* Logo/Header Section */}
        {header && <div className={styles.sidebarLogo}>{header}</div>}

        {/* Main Navigation */}
        <nav className={styles.sidebarNav}>
          {items.map((item, index) => (
            <React.Fragment key={`${item.href}-${index}`}>
              {item.label && !item.href && (
                <div className={styles.sidebarSectionLabel}>{item.label}</div>
              )}
              {item.href && (
                <button
                  className={`${styles.sidebarItem} ${item.active ? styles.active : ''}`}
                  onClick={() => onItemClick?.(item)}
                  aria-current={item.active ? 'page' : undefined}
                >
                  {item.icon && (
                    <span className={styles.sidebarItemIcon}>{item.icon}</span>
                  )}
                  <span className={styles.sidebarItemLabel}>{item.label}</span>
                  {item.badge && (
                    <span className={styles.sidebarItemBadge}>
                      {item.badge}
                    </span>
                  )}
                  {item.chevron && (
                    <svg
                      className={styles.sidebarItemChevron}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  )}
                </button>
              )}
            </React.Fragment>
          ))}
        </nav>

        {/* CTA Section */}
        {ctaTitle && (
          <div className={styles.sidebarCta}>
            <div className={styles.sidebarCtaTitle}>{ctaTitle}</div>
            {ctaDescription && (
              <div className={styles.sidebarCtaDesc}>{ctaDescription}</div>
            )}
            {ctaButtonLabel && (
              <button className={styles.sidebarCtaBtn} onClick={onCtaClick}>
                {ctaButtonLabel}
              </button>
            )}
          </div>
        )}

        {/* Footer Section */}
        {footerItems.length > 0 && (
          <div className={styles.sidebarFooter}>
            {footerItems.map((item, index) => (
              <button
                key={`footer-${item.href}-${index}`}
                className={`${styles.sidebarItem} ${item.active ? styles.active : ''}`}
                onClick={() => onItemClick?.(item)}
                aria-current={item.active ? 'page' : undefined}
              >
                {item.icon && (
                  <span className={styles.sidebarItemIcon}>{item.icon}</span>
                )}
                <span className={styles.sidebarItemLabel}>{item.label}</span>
                {item.badge && (
                  <span className={styles.sidebarItemBadge}>{item.badge}</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
);

Sidebar.displayName = 'Sidebar';

export default Sidebar;
